import { Controller } from "@hotwired/stimulus";

const LS_EXPANDED = "sidebar-expanded";
const LS_THEME = "theme";
const LS_PILL = "live-pill-style";

export default class SidebarController extends Controller {
  static targets = [
    "sidebar",
    "collapseIcon",
    "userButton",
    "userPopout",
    "themeButton",
    "themeRow",
    "themeSubmenu",
    "themeSearch",
    "themeList",
    "langButton",
    "langRow",
    "langSubmenu",
    "pillStyleButton",
  ];

  #boundKeydown = null;
  #gPending = false;
  #gTimer = null;
  #outsideClickHandler = null;

  connect() {
    this.#applyStoredState();
    this.#boundKeydown = this.#onKeydown.bind(this);
    window.addEventListener("keydown", this.#boundKeydown);
  }

  disconnect() {
    window.removeEventListener("keydown", this.#boundKeydown);
    if (this.#gTimer) clearTimeout(this.#gTimer);
    this.#removeOutsideClickListener();
  }

  // ── State restoration ────────────────────────────────────────────────────

  #applyStoredState() {
    const expanded = localStorage.getItem(LS_EXPANDED) !== "false";
    this.#setExpanded(expanded, false);
    this.#syncThemeLabel();
    this.#applyPillStyle();
  }

  // ── Expand / collapse ────────────────────────────────────────────────────

  toggleExpand() {
    const expanded = !this.sidebarTarget.classList.contains("expanded");
    this.#setExpanded(expanded, true);
  }

  #setExpanded(expanded, persist) {
    const sidebar = this.sidebarTarget;
    sidebar.classList.toggle("expanded", expanded);
    document.documentElement.setAttribute("data-sidebar-expanded", expanded);

    if (this.hasCollapseIconTarget) {
      this.collapseIconTarget.className = `fa-solid ${expanded ? "fa-angle-left" : "fa-angle-right"}`;
    }
    if (!expanded) {
      this.#closeUserPopout();
      document.dispatchEvent(new CustomEvent("sidebar:collapse"));
    }
    if (persist) localStorage.setItem(LS_EXPANDED, expanded);
  }

  // ── User popout ──────────────────────────────────────────────────────────

  toggleUserPopout() {
    if (!this.hasUserPopoutTarget) return;
    const popout = this.userPopoutTarget;
    const open = popout.classList.contains("open");
    if (open) {
      this.#closeUserPopout();
      return;
    }
    this.#closeThemeSubmenu();

    const sidebarRect = this.sidebarTarget.getBoundingClientRect();
    popout.style.left = `${sidebarRect.right + 8}px`;
    popout.classList.add("open");
    this.userButtonTargets.forEach((b) => {
      b.classList.add("active");
      b.setAttribute("aria-expanded", "true");
    });

    this.#outsideClickHandler = (e) => {
      if (!popout.contains(e.target) && !this.userButtonTargets.some((b) => b === e.target)) {
        this.#closeUserPopout();
      }
    };
    setTimeout(
      () => document.addEventListener("click", this.#outsideClickHandler, { capture: true }),
      0,
    );
  }

  #closeUserPopout() {
    if (this.hasUserPopoutTarget) this.userPopoutTarget.classList.remove("open");
    this.userButtonTargets.forEach((b) => {
      b.classList.remove("active");
      b.setAttribute("aria-expanded", "false");
    });
    this.#closeThemeSubmenu();
    this.#closeLangSubmenu();
    this.#removeOutsideClickListener();
  }

  #removeOutsideClickListener() {
    if (this.#outsideClickHandler) {
      document.removeEventListener("click", this.#outsideClickHandler, { capture: true });
      this.#outsideClickHandler = null;
    }
  }

  // ── Theme submenu ────────────────────────────────────────────────────────

  toggleThemeSubmenu() {
    if (!this.hasThemeSubmenuTarget) return;
    const open = this.themeSubmenuTarget.classList.contains("open");
    if (open) {
      this.#closeThemeSubmenu();
    } else {
      this.#openThemeSubmenu();
    }
  }

  #openThemeSubmenu() {
    if (!this.hasThemeSubmenuTarget) return;
    this.#closeLangSubmenu();
    this.themeSubmenuTarget.classList.add("open");
    if (this.hasThemeButtonTarget) this.themeButtonTarget.setAttribute("aria-expanded", "true");
    if (this.hasThemeSearchTarget) this.themeSearchTarget.focus();
    this.#syncThemeHighlight();
  }

  #closeThemeSubmenu() {
    if (this.hasThemeSubmenuTarget) this.themeSubmenuTarget.classList.remove("open");
    if (this.hasThemeButtonTarget) this.themeButtonTarget.setAttribute("aria-expanded", "false");
  }

  filterThemes() {
    if (!this.hasThemeSearchTarget || !this.hasThemeListTarget) return;
    const q = this.themeSearchTarget.value.toLowerCase();
    this.themeListTarget.querySelectorAll(".kit-theme-row").forEach((btn) => {
      btn.style.display = btn.dataset.setTheme.includes(q) ? "" : "none";
    });
  }

  #syncThemeHighlight() {
    const current = localStorage.getItem(LS_THEME) || "system";
    if (this.hasThemeListTarget) {
      this.themeListTarget.querySelectorAll(".kit-theme-row").forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.setTheme === current);
        if (btn.dataset.setTheme === current) btn.scrollIntoView({ block: "nearest" });
      });
    }
  }

  #syncThemeLabel() {
    const el = document.getElementById("theme-picker-label");
    if (!el) return;
    const theme = localStorage.getItem(LS_THEME) || "system";
    el.textContent = theme.charAt(0).toUpperCase() + theme.slice(1);
  }

  // ── Live pill style ──────────────────────────────────────────────────────

  setPillStyle(event) {
    const style = event.currentTarget.dataset.pillStyle || "outline";
    localStorage.setItem(LS_PILL, style);
    this.#applyPillStyle();
  }

  #applyPillStyle() {
    const style = localStorage.getItem(LS_PILL) || "outline";
    document.querySelectorAll(".kit-live-pill").forEach((el) => {
      el.dataset.livePillStyle = style;
    });
    if (this.hasPillStyleButtonTarget) {
      this.pillStyleButtonTargets.forEach((btn) => {
        btn.classList.toggle("active", btn.dataset.pillStyle === style);
      });
    }
  }

  // ── Language submenu ─────────────────────────────────────────────────────

  toggleLangSubmenu() {
    if (!this.hasLangSubmenuTarget) return;
    const open = this.langSubmenuTarget.classList.contains("open");
    if (open) {
      this.#closeLangSubmenu();
    } else {
      this.#openLangSubmenu();
    }
  }

  #openLangSubmenu() {
    if (!this.hasLangSubmenuTarget) return;
    this.#closeThemeSubmenu();
    this.langSubmenuTarget.classList.add("open");
    if (this.hasLangButtonTarget) this.langButtonTarget.setAttribute("aria-expanded", "true");
    if (this.hasLangRowTarget) this.langRowTargets[0]?.classList.add("active");
  }

  #closeLangSubmenu() {
    if (this.hasLangSubmenuTarget) this.langSubmenuTarget.classList.remove("open");
    if (this.hasLangButtonTarget) this.langButtonTarget.setAttribute("aria-expanded", "false");
    if (this.hasLangRowTarget) this.langRowTargets.forEach((r) => r.classList.remove("active"));
  }

  // ── Mobile drawer ────────────────────────────────────────────────────────

  openMobileDrawer() {
    document.getElementById("kit-shell")?.classList.add("mobile-drawer-open");
  }

  closeMobileDrawer() {
    document.getElementById("kit-shell")?.classList.remove("mobile-drawer-open");
  }

  // ── Keyboard shortcuts ───────────────────────────────────────────────────

  #onKeydown(e) {
    if (this.#inputFocused()) return;

    // `[` and `]` toggle sidebar
    if (e.key === "[" || e.key === "]") {
      e.preventDefault();
      this.toggleExpand();
      return;
    }

    // Esc closes all popovers
    if (e.key === "Escape") {
      this.#closeUserPopout();
      this.closeMobileDrawer();
      return;
    }

    // g-prefix nav: g then a letter jumps to the matching nav item
    if (this.#gPending) {
      clearTimeout(this.#gTimer);
      this.#gPending = false;
      if (e.key.length === 1 && /[a-z]/i.test(e.key)) {
        e.preventDefault();
        this.#jumpToShortcut(`g ${e.key.toLowerCase()}`);
      }
      return;
    }

    if (e.key === "g") {
      e.preventDefault();
      this.#gPending = true;
      this.#gTimer = setTimeout(() => {
        this.#gPending = false;
      }, 600);
    }
  }

  #jumpToShortcut(shortcut) {
    const link = document.querySelector(`[data-sidebar-shortcut="${shortcut}"]`);
    if (link) link.click();
  }

  #inputFocused() {
    const tag = document.activeElement?.tagName.toLowerCase();
    return (
      ["input", "textarea", "select"].includes(tag) || !!document.activeElement?.isContentEditable
    );
  }
}
